import {Component, OnInit} from '@angular/core';
import {ComponentHelper, FormBaseComponent, ValidatorService} from '@utils-lib';
import {AppNameEnum, NachreichungAngaben} from '@nachdigal';
import {FormBuilder} from '@angular/forms';
import {DatevMessageService} from '@datev/ngx-ui-components';
import {TranslateService} from '@ngx-translate/core';
import {ActivatedRoute} from '@angular/router';

type FormInput = {
    name: string;
    label: string;
};

@Component({
    selector: 'common-ui-nachreichung',
    templateUrl: './nachreichung.component.html'
})
export class NachreichungComponent extends FormBaseComponent<NachreichungAngaben> implements OnInit {

    formInputs: FormInput[] = [];

    private appName;

    constructor(private readonly formBuilder: FormBuilder,
                private readonly validatorService: ValidatorService,
                private readonly activatedRoute: ActivatedRoute,
                protected readonly messageService: DatevMessageService,
                protected readonly translateService: TranslateService) {
        super(messageService);
    }

    private static allExcept(filterNames: AppNameEnum[]): AppNameEnum[] {
        const result: AppNameEnum[] = [];
        for (const appName in AppNameEnum) {
            if (!filterNames.includes(AppNameEnum[appName])) {
                result.push(AppNameEnum[appName]);
            }
        }
        return result;
    }

    public ngOnInit(): void {
        super.ngOnInit();

        this.appName = ComponentHelper.getAppEnum(this.activatedRoute.snapshot);
        this.initInputs();
    }

    protected createForm(): void {
        const MAX_LENGTH = 72;
        this.form = this.formBuilder.group({
            lastschriftverfahren: [this.data.lastschriftverfahren],
            vollmachtSteuerberatung: [this.data.vollmachtSteuerberatung],
            empfangsvollmacht: [this.data.empfangsvollmacht],
            vertragUebernahmeUmwandlung: [this.data.vertragUebernahmeUmwandlung],
            weitereOrganschaftlicheVerbindungen: [this.data.weitereOrganschaftlicheVerbindungen],
            nachweisSteuerschuldnerschaftLeistungsempfaenger: [this.data.nachweisSteuerschuldnerschaftLeistungsempfaenger],
            // only KapG
            sachgruendungsbericht: [this.data.sachgruendungsbericht],
            vertragAtypischeBeteiligung: [this.data.vertragAtypischeBeteiligung],
            listeOrgangesellschaften: [this.data.listeOrgangesellschaften],
            gewinnabfuehrungsvertrag: [this.data.gewinnabfuehrungsvertrag],
            vertragTreuhandverhaeltnisse: [this.data.vertragTreuhandverhaeltnisse],
            // KapG + PersG
            gesellschaftsvertrag: [this.data.gesellschaftsvertrag],
            // only PersG
            erklaerungGesellschaftsrechtlicheVereinbarung: [this.data.erklaerungGesellschaftsrechtlicheVereinbarung],
            gesellschaftsvertragKomplementaerGmbH: [this.data.gesellschaftsvertragKomplementaerGmbH],
            vertraegeGesellschaftGesellschafter: [this.data.vertraegeGesellschaftGesellschafter],
            // only KapGAus
            auslHrgAuszug: [this.data.auslHrgAuszug],
            vertragLohnarbeitenSubUmen: [this.data.vertragLohnarbeitenSubUmen],
            genehmigungArbeitsagentur: [this.data.genehmigungArbeitsagentur],
            mietPachtLeasingVertragGEinrichtungen: [this.data.mietPachtLeasingVertragGEinrichtungen],
            letzteBilanz: [this.data.letzteBilanz],
            arbeitsvertrStaendigVertreter: [this.data.arbeitsvertrStaendigVertreter],
            vertragUnabhangigPersonen: [this.data.vertragUnabhangigPersonen],
            ansaessigkeitsbescheinigung: [this.data.ansaessigkeitsbescheinigung],
            weitereUnterlagen: [
                this.data.weitereUnterlagen, [
                    this.validatorService.maxLength(MAX_LENGTH)
                ]]
        });
    }

    private initInputs(): void {
      let vertragUebernahmeUmwandlungLabel = 'COMMON_UI.FRAGEBOGEN.NACHREICHUNG.VERTRAGUEBERNAHMEUMWANDLUNG';
      let gesellschaftsVertragLabel = 'COMMON_UI.FRAGEBOGEN.NACHREICHUNG.GESELLSCHAFTSVERTRAG';

      if (this.appName === AppNameEnum.FragebogenKapG) {
        vertragUebernahmeUmwandlungLabel = 'COMMON_UI.FRAGEBOGEN.NACHREICHUNG_KAPG.VERTRAGUEBERNAHMEUMWANDLUNG';
        gesellschaftsVertragLabel = 'COMMON_UI.FRAGEBOGEN.NACHREICHUNG_KAPG.GESELLSCHAFTSVERTRAG';
      }

      this.addInput('lastschriftverfahren', 'COMMON_UI.FRAGEBOGEN.NACHREICHUNG.LASTSCHRIFTVERFAHREN');
      this.addInput('vollmachtSteuerberatung', 'COMMON_UI.FRAGEBOGEN.NACHREICHUNG.VOLLMACHTSTEUERBERATUNG');
      this.addInput('empfangsvollmacht', 'COMMON_UI.FRAGEBOGEN.NACHREICHUNG.EMPFANGSVOLLMACHT', [
        AppNameEnum.FragebogenEUn, AppNameEnum.FragebogenBetPG,
        AppNameEnum.FragebogenPersG, AppNameEnum.FragebogenKapG, AppNameEnum.FragebogenKapGAus]);
      this.addInput('vertragUebernahmeUmwandlung', vertragUebernahmeUmwandlungLabel,
          NachreichungComponent.allExcept([AppNameEnum.FragebogenBetPG, AppNameEnum.FragebogenKapGAus]));
      this.addInput('weitereOrganschaftlicheVerbindungen', 'COMMON_UI.FRAGEBOGEN.NACHREICHUNG.WEITEREORGANSCHAFTLICHEVERBINDUNGEN',
          NachreichungComponent.allExcept([AppNameEnum.FragebogenBetPG, AppNameEnum.FragebogenKapGAus,
              AppNameEnum.FragebogenEUn, AppNameEnum.FragebogenPersG, AppNameEnum.FragebogenKapG]));
      this.addInput('ansaessigkeitsbescheinigung', 'COMMON_UI.FRAGEBOGEN.NACHREICHUNG.ANSAESSIGKEITSBESCHEINIGUNG',
          [AppNameEnum.FragebogenKapGAus]);
      this.addInput('nachweisSteuerschuldnerschaftLeistungsempfaenger',
          'COMMON_UI.FRAGEBOGEN.NACHREICHUNG.NACHWEISSTEUERSCHULDNERSCHAFTLEISTUNGSEMPFAENGER',
          NachreichungComponent.allExcept([AppNameEnum.FragebogenBetPG, AppNameEnum.FragebogenKapGAus,
              AppNameEnum.FragebogenEUn, AppNameEnum.FragebogenPersG, AppNameEnum.FragebogenKapG]));
      this.addInput('sachgruendungsbericht', 'COMMON_UI.FRAGEBOGEN.NACHREICHUNG.SACHGRUENDUNGSBERICHT',
          [AppNameEnum.FragebogenKapG]);
      this.addInput('vertragAtypischeBeteiligung', 'COMMON_UI.FRAGEBOGEN.NACHREICHUNG.ATYPISCHESTILLEBETEILIGUNG',
          [AppNameEnum.FragebogenKapG, AppNameEnum.FragebogenKapGAus]);
      this.addInput('listeOrgangesellschaften', 'COMMON_UI.FRAGEBOGEN.NACHREICHUNG.LISTEORGANGESELLSCHAFTEN',
          [AppNameEnum.FragebogenKapG, AppNameEnum.FragebogenKapGAus]);
      this.addInput('gewinnabfuehrungsvertrag', 'COMMON_UI.FRAGEBOGEN.NACHREICHUNG.GEWINNABFUEHRUNGSVERTRAG',
          [AppNameEnum.FragebogenKapG, AppNameEnum.FragebogenKapGAus]);
      this.addInput('vertragTreuhandverhaeltnisse', 'COMMON_UI.FRAGEBOGEN.NACHREICHUNG.TREUHANDVERHAELTNISSE',
          [AppNameEnum.FragebogenKapG, AppNameEnum.FragebogenKapGAus]);
      this.addInput('gesellschaftsvertrag', gesellschaftsVertragLabel,
          NachreichungComponent.allExcept([AppNameEnum.FragebogenEUn]));
      this.addInput('vertraegeGesellschaftGesellschafter', 'COMMON_UI.FRAGEBOGEN.NACHREICHUNG.VERTRAEG_GESELLSCHAFT_GESELLSCHAFTER',
          [AppNameEnum.FragebogenPersG, AppNameEnum.FragebogenKapG, AppNameEnum.FragebogenKapGAus]);
      this.addInput('erklaerungGesellschaftsrechtlicheVereinbarung',
          'COMMON_UI.FRAGEBOGEN.NACHREICHUNG.ERKLAERUNG_GESELLSCHAFTLICHE_VEREINBARUNGEN',
          [AppNameEnum.FragebogenPersG]);
      this.addInput('gesellschaftsvertragKomplementaerGmbH', 'COMMON_UI.FRAGEBOGEN.NACHREICHUNG.GESELLSCHAFTSVERTRAG_KOMPLEMENTAER_GMBH',
          [AppNameEnum.FragebogenPersG]);
      this.addInput('auslHrgAuszug', 'COMMON_UI.FRAGEBOGEN.NACHREICHUNG.AUSLANDISCHEMREGISTER',
          [AppNameEnum.FragebogenKapGAus]);
      this.addInput('vertragLohnarbeitenSubUmen', 'COMMON_UI.FRAGEBOGEN.NACHREICHUNG.VERTRAGUBERREGIE',
          [AppNameEnum.FragebogenKapGAus]);
      this.addInput('genehmigungArbeitsagentur', 'COMMON_UI.FRAGEBOGEN.NACHREICHUNG.GENEHMIGUNGSBESCHEID',
          [AppNameEnum.FragebogenKapGAus]);
      this.addInput('mietPachtLeasingVertragGEinrichtungen', 'COMMON_UI.FRAGEBOGEN.NACHREICHUNG.VERTRAGKAUFMIETE',
          [AppNameEnum.FragebogenKapGAus]);
      this.addInput('letzteBilanz', 'COMMON_UI.FRAGEBOGEN.NACHREICHUNG.BILANZ',
          [AppNameEnum.FragebogenKapGAus]);
      this.addInput('arbeitsvertrStaendigVertreter', 'COMMON_UI.FRAGEBOGEN.NACHREICHUNG.ARBEITSVERTRAG',
          [AppNameEnum.FragebogenKapGAus]);
      this.addInput('vertragUnabhangigPersonen', 'COMMON_UI.FRAGEBOGEN.NACHREICHUNG.VERTRAGPERSONEN',
          [AppNameEnum.FragebogenKapGAus]);
    }

    private addInput(name: string, label: string, showOnlyIn?: AppNameEnum[]): void {
        if (!showOnlyIn || showOnlyIn.includes(this.appName)) {
            this.formInputs.push({name, label});
        }
    }
}
